import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { Box, Button, Link, Typography } from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { useGetAgentQuery } from '../../api/agents.api';
import { useTrackEvent } from '../../analytics/useTrackEvent';
import { AgentVersionList } from '../../components/AgentVersionList';
import { ContentSection } from '../../components/ContentSection';
import { ContentState } from '../../components/ContentState';
import { CopyButton } from '../../components/CopyButton';
import { EvaluationTabs } from '../../components/EvaluationTabs';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { ReturnLink } from '../../components/ReturnLink';
import { TextBlock } from '../../components/TextBlock';
import { VersionCompare } from '../../components/VersionCompare';
import { VersionDiff } from '../../components/VersionDiff';
import { SDLC_PATH, TOOLKIT_PATH } from '../../config';
import { cardSurfaceSx, contentWidthSx, pageGutterSx, sectionSpacingSx, tokens } from '../../theme';

/** The query-string key that picks a version. Absent means the newest. */
const VERSION_PARAM = 'version';

/** The version being compared against. Absent means none, and so no comparison. */
const COMPARE_PARAM = 'compare';

/**
 * `/toolkit/agents/:name` — one agent, in the manner of a package page: the
 * newest version by default, every version listed beside it, and the prompt of
 * whichever is on screen.
 *
 * **The whole history arrives in one request**, so choosing a version is a change
 * to the query string and not a loading state. The version is in the URL rather
 * than in component state because a particular version is something people link
 * to, and component state survives neither a paste nor a reload.
 *
 * **An unknown version shows the newest, and says so.** A link to a version that
 * was never released should not be a dead end, and it should not silently show
 * something other than what the address asked for either.
 *
 * **Run in Harness is drawn and disabled, and says why.** It is a promise the page
 * keeps a place for: a native `disabled` button is out of the tab order, which is
 * what a placeholder is meant to be, and the words beside it are what a disabled
 * control cannot give by itself.
 */
export const AgentPage = () => {
  const { name = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { data: agent, isLoading, error, refetch } = useGetAgentQuery(name);
  const trackEvent = useTrackEvent();

  const requested = params.get(VERSION_PARAM);
  const found = agent?.versions.find((entry) => entry.version === requested);
  // The service returns them newest first, so the first is the default.
  const selected = found ?? agent?.versions[0];
  const fellBack = requested !== null && found === undefined;

  // **Ignored rather than reported when it makes no sense**: an unknown version, or the
  // one already on screen, which differs from itself in nothing. A link with a stale
  // compare in it should still open the page.
  const comparedWith = params.get(COMPARE_PARAM);
  const compared = agent?.versions.find(
    (entry) => entry.version === comparedWith && entry.version !== selected?.version,
  );

  /** Rewrites one parameter and keeps the rest, replacing the entry so a choice is not a back-button press. */
  const compareWith = (version: string | null) => {
    const next = new URLSearchParams(params);
    if (version === null) {
      next.delete(COMPARE_PARAM);
    } else {
      next.set(COMPARE_PARAM, version);
    }
    setParams(next, { replace: true });
  };

  return (
    <Box sx={pageGutterSx}>
      <Box sx={{ ...contentWidthSx, paddingBlock: 12 }}>
        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorTitle="Unable to load this agent"
          errorNoun="agent"
          skeleton={<LoadingSkeleton layout="agent" label="Loading the agent" />}
        >
          {agent !== undefined && selected !== undefined && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <ReturnLink to={TOOLKIT_PATH} label="the agent library" />

                <Typography
                  variant="overline"
                  component="p"
                  sx={{ color: 'text.secondary', letterSpacing: '1px' }}
                >
                  Agent &middot; Version {selected.version}
                  {selected.version === agent.latestVersion ? ' (latest)' : ''}
                </Typography>

                <Typography variant="h1" component="h1">
                  {selected.title}
                </Typography>

                <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 720 }}>
                  {selected.summary}
                </Typography>

                {fellBack && (
                  <Typography role="status" variant="body2" sx={{ color: 'text.secondary' }}>
                    There is no version {requested} of this agent, so this is the latest.
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  ...sectionSpacingSx,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 936fr) minmax(0, 400fr)' },
                  columnGap: 8,
                  rowGap: 6,
                  alignItems: 'start',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <ContentSection
                    heading="Prompt"
                    caption={`The prompt as released in version ${selected.version}.`}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                        <CopyButton
                          text={selected.prompt}
                          label="Copy prompt"
                          onCopy={() => {
                            trackEvent({
                              name: 'copy_prompt',
                              params: { agent_name: agent.name, agent_version: selected.version },
                            });
                          }}
                        />

                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled
                            startIcon={<PlayArrowOutlinedIcon aria-hidden fontSize="small" />}
                          >
                            Run in Harness
                          </Button>
                          <Typography variant="body2" sx={{ color: 'text.meta', fontSize: 12 }}>
                            Coming soon
                          </Typography>
                        </Box>
                      </Box>

                      <TextBlock label="Prompt" maxHeight={560}>
                        {selected.prompt}
                      </TextBlock>
                    </Box>
                  </ContentSection>

                  {compared !== undefined && (
                    <ContentSection
                      heading={`Changes from ${compared.version} to ${selected.version}`}
                      caption={`Lines only in ${selected.version} are marked + and lines only in ${compared.version} are marked \u2212 and struck through.`}
                    >
                      <VersionDiff from={compared} to={selected} />
                    </ContentSection>
                  )}

                  {selected.evaluations.length > 0 && (
                    <ContentSection
                      heading="Evaluations"
                      caption="Each evaluation is an input and a description of a good output. Nothing runs them yet, so they are expectations, not results."
                    >
                      {/* Keyed on the version, so choosing another opens on its first
                          case rather than on one it may not have. */}
                      <EvaluationTabs key={selected.version} evaluations={selected.evaluations} />
                    </ContentSection>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
                    <ContentSection heading="Versions" level={3}>
                      <AgentVersionList
                        agentName={agent.name}
                        versions={agent.versions.map((entry) => entry.version)}
                        selected={selected.version}
                      />

                      {/* Only for more than one version: with one there is nothing to
                          compare, and the area is absent rather than empty. */}
                      {agent.versions.length > 1 && (
                        <Box sx={{ marginTop: 6 }}>
                          <VersionCompare
                            versions={agent.versions.map((entry) => entry.version)}
                            selected={selected.version}
                            value={compared?.version ?? null}
                            onChange={compareWith}
                          />
                        </Box>
                      )}
                    </ContentSection>
                  </Box>

                  {selected.methods.length > 0 && (
                    <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
                      <ContentSection
                        heading="Supports"
                        caption="The methods this agent helps with"
                        level={3}
                      >
                        <Box
                          component="ul"
                          sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                          {selected.methods.map((method) => (
                            <Box component="li" key={method.id}>
                              <RouterLink
                                to={`${SDLC_PATH}/${method.path}`}
                                style={{ textDecoration: 'none' }}
                              >
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{ color: 'text.link', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {method.name}
                                </Typography>
                              </RouterLink>
                            </Box>
                          ))}
                        </Box>
                      </ContentSection>
                    </Box>
                  )}

                  <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
                    <ContentSection heading="Source" level={3}>
                      {/* **Only an https address becomes a link.** A prompt's source is its address in
                          Prompt Library; an agent's is a path in a repository, which is not one. A
                          string of any other scheme stays text, so nothing reaching here can be a
                          `javascript:` link. */}
                      {selected.sourcePath.startsWith('https://') ? (
                        <Link
                          href={selected.sourcePath}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ fontFamily: tokens.fontFamily.mono, overflowWrap: 'anywhere' }}
                        >
                          {selected.sourcePath}
                        </Link>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: tokens.fontFamily.mono, color: 'text.secondary', overflowWrap: 'anywhere' }}
                        >
                          {selected.sourcePath}
                        </Typography>
                      )}
                    </ContentSection>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </ContentState>
      </Box>
    </Box>
  );
};
